import { SipClientAnalyticsEvents } from '~/types/Analytics/SipClientAnalyticsEvents'

import { RegistererState, SessionState } from '@mangovoice/sip'

/**
 * Curated call lifecycle events. These replace the per-transition
 * `reg_state_ch_*` / `ob_sess_state_ch_*` / `in_sess_state_ch_*` firehose with a
 * small set of queryable domain events, so call connect-success, failure, and
 * churn rates can be tracked (the signals needed to catch call-UX regressions).
 *
 * They flow through the `sipc` Analytics namespace, so PostHog sees them as
 * `sipc_sip_registered`, `sipc_call_connected`, etc. Softphone and deskphone
 * share these names; `transport` splits them. Properties are PHI-clean:
 * `direction`, `callUuid`, `connected`, `transport`, and `durationMs` on stop
 * — never dialed numbers or names.
 *
 * Values are `SipClientAnalyticsEvents` members so SIP `Analytics.track` stays
 * on the registry.
 */
export const SipAnalyticsEvent = {
  Registered: SipClientAnalyticsEvents.SipRegistered,
  /** Outbound INVITE sent — the denominator for call connect-success rate. */
  CallPlaced: SipClientAnalyticsEvents.CallPlaced,
  /**
   * Inbound 180 Ringing sent from progressCall — this client actually rang.
   * Not every SIP INVITE: auto-reject / UA-down paths never progress.
   */
  InviteReceived: SipClientAnalyticsEvents.InvReceived,
  CallConnected: SipClientAnalyticsEvents.CallConnected,
  CallEnded: SipClientAnalyticsEvents.CallEnded,
  CallFailed: SipClientAnalyticsEvents.CallFailed,
} as const

export const CallTransport = {
  Softphone: 'softphone',
  Deskphone: 'deskphone',
} as const

export type CallTransport = (typeof CallTransport)[keyof typeof CallTransport]

export interface CallLifecycleProperties {
  callUuid: string
  direction: 'inbound' | 'outbound'
  transport: CallTransport
  connected?: boolean
  canceled?: boolean
  durationMs?: number
}

/**
 * Maps a registration state change to a domain event, or null for states not
 * worth an event. Unregistered is deliberate lifecycle plumbing (logout,
 * teardown) with no analytics consumer; failed registrations are covered by
 * sipc_reg_failure.
 */
export const mapRegistrationStateToEvent = (
  state: RegistererState,
): SipClientAnalyticsEvents | null => {
  switch (state) {
    case RegistererState.Registered:
      return SipAnalyticsEvent.Registered
    default:
      return null
  }
}

export interface SessionTransitionEvent {
  event: SipClientAnalyticsEvents
  /** Whether the call had reached an established media session before ending. */
  connected: boolean
  /**
   * Only on `call_failed`: this side tore the call down before it reached
   * media, rather than the call failing on its own. Read it as "not a
   * failure".
   */
  canceled?: boolean
}

export interface SessionTransitionContext {
  direction: 'inbound' | 'outbound'
  /** Whether this call ever reached `Established`. */
  wasEstablished: boolean
  /**
   * Whether this side tore the call down. The caller has to track it: only an
   * outbound cancel passes through `Terminating`, while `Invitation.reject()`
   * and an incoming CANCEL both go straight to `Terminated`.
   */
  locallyTerminated: boolean
  /** Inbound only: `accept()` ran on this client and no CANCEL arrived. */
  answerAttempted?: boolean
}

/**
 * True when a call that never connected on this device is reported as
 * `call_failed` without further context. Inbound calls are not.
 */
export const reportsUnconnectedCallAsFailed = (
  direction: 'inbound' | 'outbound',
): boolean => direction === 'outbound'

/**
 * Maps a session state transition to a domain event:
 * - `Established`                     -> `call_connected`
 * - `Terminated` (was up)             -> `call_ended`   (normal hangup after connect)
 * - `Terminated` (never up, outbound) -> `call_failed`  (setup never reached media),
 *                                        with `canceled` telling the two kinds apart
 * - `Terminated` (never up, inbound)  -> `call_failed` if `answerAttempted`, else null
 * Intermediate transitions (Initial / Establishing / Terminating) return null.
 *
 * Both context flags have to be tracked by the caller rather than read off the
 * previous state. SIP.js routes an outbound cancel through `Terminating`
 * whether or not media was ever up, so `Establishing -> Terminating ->
 * Terminated` (user gave up while it rang) and `Established -> Terminating ->
 * Terminated` (normal hangup) are indistinguishable from the transition alone.
 * Inbound is the mirror problem: a declined call and one the remote side
 * canceled both go straight to `Terminated`, so only the caller knows which
 * side hung up.
 */
export const mapSessionTransitionToEvent = (
  newState: SessionState,
  {
    direction,
    wasEstablished,
    locallyTerminated,
    answerAttempted = false,
  }: SessionTransitionContext,
): SessionTransitionEvent | null => {
  if (newState === SessionState.Established) {
    return { event: SipAnalyticsEvent.CallConnected, connected: true }
  }
  if (newState === SessionState.Terminated) {
    if (wasEstablished) {
      return { event: SipAnalyticsEvent.CallEnded, connected: true }
    }
    if (!answerAttempted && !reportsUnconnectedCallAsFailed(direction)) {
      return null
    }
    return {
      event: SipAnalyticsEvent.CallFailed,
      connected: false,
      canceled: locallyTerminated,
    }
  }
  return null
}

/**
 * How long a deskphone UUID stays in the terminal replay window after
 * onStopped. Replayed LINE_EXTENSION_HANGUP (and ANSWERED) in this window
 * are dropped; after it, that UUID's terminal entry is gone.
 */
export const DESKPHONE_TERMINAL_REPLAY_WINDOW_MS = 60_000

/**
 * How long a deskphone UUID may stay in the connect maps after onConnected
 * without onStopped. A dropped LINE_EXTENSION_HANGUP would otherwise leave
 * the UUID until the tab closes. Longer than a typical call so a late hangup
 * still maps to call_ended.
 */
export const DESKPHONE_CONNECTED_STALE_MS = 24 * 60 * 60 * 1000

/**
 * True when a chat-rtm RINGING/ANSWERED/HANGUP belongs to this user's
 * deskphone, not a SIP call that already emits sipc_call_*.
 *
 * `hasSipSession` is not enough on HANGUP: SIP Terminated deletes the
 * session (and usePhone may already have removed the call) before the
 * WebSocket message arrives, so an unknown callSource would look like a
 * deskphone failure. `hadRecentSipSession` positively excludes those UUIDs.
 */
export const shouldEmitDeskphoneLifecycle = ({
  shouldHandle,
  hasSipSession,
  hadRecentSipSession = false,
  callSource,
}: {
  shouldHandle: boolean
  hasSipSession: boolean
  hadRecentSipSession?: boolean
  callSource?: 'softphone' | 'deskphone'
}): boolean =>
  shouldHandle &&
  !hasSipSession &&
  !hadRecentSipSession &&
  callSource !== 'softphone'

export interface DeskphoneLifecycleInput {
  callUuid: string
  direction: 'inbound' | 'outbound'
  startedAtMs?: number
  nowMs?: number
}

export interface DeskphoneLifecycleEmission {
  event: SipClientAnalyticsEvents
  properties: CallLifecycleProperties
}

export const toAnalyticsData = (
  properties: CallLifecycleProperties,
): Record<string, unknown> => ({ ...properties })

/**
 * One connect and one terminal event per deskphone call UUID; an inbound call
 * that never connected here has no terminal event. LINE_EXTENSION_*
 * messages can replay after a reconnect; duplicates in the terminal window
 * are dropped. onStopped clears that UUID's connect maps. Connect maps are
 * also dropped if a connect is older than DESKPHONE_CONNECTED_STALE_MS
 * without a stop (dropped HANGUP).
 */
export class DeskphoneCallLifecycleTracker {
  private readonly connectedAtMs = new Map<string, number>()
  private readonly connectedEmitted = new Set<string>()
  private readonly terminalSeenUntilMs = new Map<string, number>()

  onConnected(
    input: DeskphoneLifecycleInput,
  ): DeskphoneLifecycleEmission | null {
    const nowMs = input.nowMs ?? Date.now()
    this.pruneStaleState(nowMs)
    if (this.hasRecentTerminal(input.callUuid, nowMs)) {
      return null
    }
    if (this.connectedEmitted.has(input.callUuid)) {
      return null
    }

    this.connectedEmitted.add(input.callUuid)
    this.connectedAtMs.set(input.callUuid, nowMs)

    return {
      event: SipAnalyticsEvent.CallConnected,
      properties: {
        callUuid: input.callUuid,
        direction: input.direction,
        transport: CallTransport.Deskphone,
        connected: true,
      },
    }
  }

  onStopped(input: DeskphoneLifecycleInput): DeskphoneLifecycleEmission | null {
    const nowMs = input.nowMs ?? Date.now()
    if (this.hasRecentTerminal(input.callUuid, nowMs)) {
      return null
    }

    const wasConnected = this.connectedEmitted.has(input.callUuid)
    const startMs = this.connectedAtMs.get(input.callUuid) ?? input.startedAtMs
    const durationMs =
      startMs === undefined ? undefined : Math.max(0, nowMs - startMs)

    this.connectedEmitted.delete(input.callUuid)
    this.connectedAtMs.delete(input.callUuid)
    this.terminalSeenUntilMs.set(
      input.callUuid,
      nowMs + DESKPHONE_TERMINAL_REPLAY_WINDOW_MS,
    )
    this.pruneStaleConnected(nowMs)

    if (!wasConnected && !reportsUnconnectedCallAsFailed(input.direction)) {
      return null
    }

    return {
      event: wasConnected
        ? SipAnalyticsEvent.CallEnded
        : SipAnalyticsEvent.CallFailed,
      properties: {
        callUuid: input.callUuid,
        direction: input.direction,
        transport: CallTransport.Deskphone,
        connected: wasConnected,
        ...(durationMs === undefined ? {} : { durationMs }),
      },
    }
  }

  private hasRecentTerminal(callUuid: string, nowMs: number): boolean {
    this.pruneExpiredTerminals(nowMs)
    const untilMs = this.terminalSeenUntilMs.get(callUuid)
    return untilMs !== undefined && untilMs > nowMs
  }

  private pruneStaleState(nowMs: number): void {
    this.pruneExpiredTerminals(nowMs)
    this.pruneStaleConnected(nowMs)
  }

  private pruneExpiredTerminals(nowMs: number): void {
    for (const [uuid, untilMs] of this.terminalSeenUntilMs) {
      if (untilMs <= nowMs) {
        this.terminalSeenUntilMs.delete(uuid)
      }
    }
  }

  private pruneStaleConnected(nowMs: number): void {
    for (const [uuid, startMs] of this.connectedAtMs) {
      if (nowMs - startMs >= DESKPHONE_CONNECTED_STALE_MS) {
        this.connectedAtMs.delete(uuid)
        this.connectedEmitted.delete(uuid)
      }
    }
  }
}
